import React, { useMemo } from 'react';
import { formatMessage } from 'devextreme/localization';

import { buildGraph, GraphNode, TransformationStep } from './complexTransformationSteps';

interface ComplexTransformationGraphProps {
    steps: TransformationStep[];
    getFieldLabel: (id: string) => string;
    // Text of the output block.
    targetLabel: string;
}

// Fields and constants (inputs) and the target field (output) are "tag" blocks pointing right;
// a transformation is a square block with one input port per field/constant it receives.
const TAG_WIDTH = 150;
const TAG_HEIGHT = 24;
const TAG_TIP = 10;
const STEP_WIDTH = 110;
const STEP_MIN_HEIGHT = 56;
const PORT_SPACING = 14;
const COLUMN_WIDTH = TAG_WIDTH;
const COLUMN_GAP = 44;
const ROW_GAP = 6;
const PADDING = 6;
const PORT_RADIUS = 3;

const truncate = (text: string, max: number): string => text.length > max ? text.slice(0, max - 1) + '…' : text;

// Only a transformation that merges several inputs (fields and/or a constant) is a square block with ports;
// a single-input one is drawn inline as a tag, like the fields around it.
const isMerge = (node: GraphNode): boolean => node.Kind === 'step' && node.Ports >= 2;
const nodeWidth = (node: GraphNode): number => isMerge(node) ? STEP_WIDTH : TAG_WIDTH;
const nodeHeight = (node: GraphNode): number =>
    isMerge(node) ? Math.max(STEP_MIN_HEIGHT, node.Ports * PORT_SPACING + 8) : TAG_HEIGHT;

// Input ports sit on the left side of a step, evenly spread.
const portOffset = (node: GraphNode, port: number): number => nodeHeight(node) * (port + 1) / (node.Ports + 1);

const displayText = (node: GraphNode): string => {
    const field = formatMessage("ComplexTransformationPopup_FieldPrefix");
    switch (node.Kind) {
        case 'source':
            return `${field} : "${node.Title}"`;
        case 'constant':
            return `${formatMessage("ComplexTransformationPopup_ConstantPrefix")} : ${node.Title}`;
        default:
            return node.Title;
    }
};

// Left-to-right flow of the generated transformation: inputs → transformations → target field.
// Colours come from CSS (see .complex-transformation-graph in TransformationPopup.css) so it follows the theme.
const ComplexTransformationGraph: React.FC<ComplexTransformationGraphProps> = ({ steps, getFieldLabel, targetLabel }) => {

    const graph = useMemo(() => buildGraph(steps, getFieldLabel, targetLabel), [steps, getFieldLabel, targetLabel]);

    const { positions, width, height } = useMemo(() => {
        const positions = new Map<string, { x: number; y: number }>();
        const columnY = new Map<number, number>();
        // Slots are evenly spaced with the height of a tag: the inputs (integer slots) all get the same gap, whatever
        // the size of the merge blocks, which are centred on the (fractional) average slot of their inputs.
        const slotHeight = TAG_HEIGHT + ROW_GAP;
        // A merge block taller than the span of its inputs would start above the top edge: shift everything down instead
        // of clamping it, so every block stays centred on its inputs.
        const centredTop = (node: GraphNode): number => node.Slot * slotHeight + (TAG_HEIGHT - nodeHeight(node)) / 2;
        const topOverflow = Math.max(0, -Math.min(...graph.Nodes.map(centredTop)));
        let maxY = 0;
        [...graph.Nodes].sort((a, b) => (a.Column - b.Column) || (a.Slot - b.Slot)).forEach((node) => {
            // Centre the block on its slot; never overlap the previous block of the same column.
            const desired = PADDING + topOverflow + centredTop(node);
            const y = Math.max(desired, columnY.get(node.Column) ?? PADDING);
            // Steps are narrower than a column: centre them so the links stay symmetrical.
            const x = PADDING + node.Column * (COLUMN_WIDTH + COLUMN_GAP) + (COLUMN_WIDTH - nodeWidth(node)) / 2;
            positions.set(node.Key, { x, y });
            const bottom = y + nodeHeight(node);
            columnY.set(node.Column, bottom + ROW_GAP);
            maxY = Math.max(maxY, bottom);
        });
        return {
            positions,
            width: PADDING * 2 + graph.Columns * COLUMN_WIDTH + (graph.Columns - 1) * COLUMN_GAP,
            height: maxY + PADDING,
        };
    }, [graph]);

    const byKey = new Map(graph.Nodes.map((n) => [n.Key, n]));

    return (
        <div className="complex-transformation-graph">
            {/* Scaled down to fit the available area (never up), so the whole graph stays visible without scrolling. */}
            <svg viewBox={`0 0 ${width} ${height}`} width="100%" height="100%" preserveAspectRatio="xMidYMid meet"
                style={{ maxWidth: width, maxHeight: height }} role="img">
                <defs>
                    <marker id="complex-transformation-arrow" viewBox="0 0 10 10" refX="9" refY="5"
                        markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                        <path d="M 0 0 L 10 5 L 0 10 z" className="complex-transformation-arrow" />
                    </marker>
                </defs>

                {graph.Edges.map((edge) => {
                    const from = byKey.get(edge.From)!;
                    const to = byKey.get(edge.To)!;
                    const a = positions.get(edge.From)!;
                    const b = positions.get(edge.To)!;
                    const x1 = a.x + nodeWidth(from);
                    const y1 = a.y + nodeHeight(from) / 2;
                    const x2 = b.x - (isMerge(to) ? PORT_RADIUS : 2);
                    const y2 = b.y + (isMerge(to) ? portOffset(to, edge.ToPort) : nodeHeight(to) / 2);
                    const mid = (x1 + x2) / 2;
                    return (
                        <path key={`${edge.From}->${edge.To}:${edge.ToPort}`}
                            d={`M ${x1} ${y1} C ${mid} ${y1}, ${mid} ${y2}, ${x2} ${y2}`}
                            className="complex-transformation-edge"
                            markerEnd="url(#complex-transformation-arrow)" />
                    );
                })}

                {graph.Nodes.map((node) => {
                    const { x, y } = positions.get(node.Key)!;
                    const w = nodeWidth(node);
                    const h = nodeHeight(node);
                    const text = displayText(node);
                    return (
                        <g key={node.Key} transform={`translate(${x} ${y})`} className={`complex-transformation-node ${node.Kind}`}>
                            <title>{node.Subtitle ? `${text}
${node.Subtitle}` : text}</title>
                            {isMerge(node) ? (
                                <>
                                    <rect width={w} height={h} />
                                    {Array.from({ length: node.Ports }, (_, port) => (
                                        <circle key={port} cx={0} cy={portOffset(node, port)} r={PORT_RADIUS} className="complex-transformation-port" />
                                    ))}
                                    <text x={w / 2} y={node.Subtitle ? h / 2 - 3 : h / 2 + 4} textAnchor="middle" className="complex-transformation-node-title">
                                        {truncate(text, 15)}
                                    </text>
                                    {node.Subtitle && (
                                        <text x={w / 2} y={h / 2 + 11} textAnchor="middle" className="complex-transformation-node-subtitle">
                                            {truncate(node.Subtitle, 17)}
                                        </text>
                                    )}
                                </>
                            ) : (
                                <>
                                    <path d={`M 0 0 H ${w - TAG_TIP} L ${w} ${h / 2} L ${w - TAG_TIP} ${h} H 0 Z`} />
                                    <text x={10} y={h / 2 + 4} className="complex-transformation-node-title">
                                        {truncate(node.Subtitle ? `${text} · ${node.Subtitle}` : text, 21)}
                                    </text>
                                </>
                            )}
                        </g>
                    );
                })}
            </svg>
        </div>
    );
};

export default ComplexTransformationGraph;
